import { inject, Injectable } from '@angular/core';

import { TellentHttpApiService } from '@tellent/http';
import {
  APP_GUID,
  InviteMemberPayload,
  ManageSeatsParamsPayload,
  ManageSeatsResponse,
  MemberDataUpdate,
  TeamMembersPaginationParams,
  TellentMember,
  TellentMemberResponse,
  TellentMembersResponse,
} from '@tellent/types';
import { map, Observable } from 'rxjs';

import { ID } from '@recruitee/ngrx';

@Injectable({ providedIn: 'root' })
export class TellentMembersHttpClient {
  private appGuid = inject(APP_GUID);
  constructor(protected httpApi: TellentHttpApiService) {}

  public inviteTellentMember(payload: InviteMemberPayload): Observable<TellentMember[]> {
    return this.httpApi
      .post('/members/add', null, payload)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public inviteAppMember(payload: InviteMemberPayload): Observable<TellentMember[]> {
    return this.httpApi
      .post(`/api/app/${this.appGuid}/members/add`, null, payload, { skipCompany: true })
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchMemberByEmail(email: string): Observable<TellentMember[]> {
    return this.httpApi
      .get('/members', { email }, null)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchAppMemberByEmail(email: string): Observable<TellentMember[]> {
    return this.httpApi
      .get(`/api/app/${this.appGuid}/members`, { email }, { skipCompany: true })
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchMemberByGuid(guid: ID | string): Observable<TellentMemberResponse> {
    return this.httpApi.get(`/members/${guid}`);
  }

  public fetchMembers(params: TeamMembersPaginationParams): Observable<TellentMembersResponse> {
    const { page, limit, q, statuses, sortBy, sortOrder } = params;
    return this.httpApi.get('/members', {
      page,
      limit,
      q,
      statuses,
      sort_by: sortBy,
      sort_dir: sortOrder,
    });
  }

  public deactivateMember(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${payload.guid}/deactivate`);
  }

  public reactivateMember(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${payload.guid}/reactivate`);
  }

  public resendInvitation(payload: { id: number }): Observable<TellentMemberResponse> {
    return this.httpApi.post(`/invitations/${payload.id}/resend`);
  }

  public sendInvitation(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.post(`/invitations`, null, { memberGuid: payload.guid });
  }
  public manageSeats(payload: ManageSeatsParamsPayload): Observable<ManageSeatsResponse> {
    return this.httpApi.post(`/seats/manage`, null, payload);
  }

  public updateMemberData(payload: MemberDataUpdate): Observable<TellentMemberResponse> {
    const formData = new FormData();

    Object.entries(payload).forEach(
      ([key, value]: [string, string | boolean | File | undefined]) => {
        // eslint-disable-next-line eqeqeq
        if (value == undefined) return;
        if (key === 'guid') return;

        const isFile = value instanceof File;

        formData.append(`${key}`, isFile ? <File>value : `${value}`);
      },
    );

    return this.httpApi.patch(`/members/${payload.guid}/update`, null, formData);
  }

  public sendPasswordResetInstructions(userGuid: string): Observable<void> {
    return this.httpApi.post(`/users/${userGuid}/reset-password`);
  }
}
